import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box, Typography, Card, CardContent, Alert, TextField, InputAdornment,
  MenuItem, Select, FormControl, InputLabel, Button, Chip, IconButton, Tooltip,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import PhoneIcon from "@mui/icons-material/Phone";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import SpeedIcon from "@mui/icons-material/Speed";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import SearchIcon from "@mui/icons-material/Search";
import CallMadeIcon from "@mui/icons-material/CallMade";
import CallReceivedIcon from "@mui/icons-material/CallReceived";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import { useNavigate } from "react-router-dom";
import SFDateRangeSelector from "@/components/inputs/SFDateRangeSelector";
import { phappsRequest } from "@/services/apiService";
import { API_PATHS } from "@/config/apiConstants";
import { pharmacyHaloDataGridSx } from "@/utils/adminTableStyles";
import CallDetailModal from "@/components/admin/dialpad/CallDetailModal";
import AllocateCallModal from "@/components/admin/dialpad/AllocateCallModal";

const DIALPAD_PINK = "#E63888";
const DIALPAD_PURPLE = "#7B2FBE";
const DIALPAD_ORANGE = "#F58A2E";
const DIALPAD_LIGHT = "#FDE8F3";

function fmtDuration(s) {
  if (!s) return "—";
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return m > 0 ? `${m}m ${s % 60}s` : `${s}s`;
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-AU", {
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

function DialpadStatCard({ title, value, sub, Icon, accent = DIALPAD_PINK }) {
  return (
    <Card
      sx={{
        borderRadius: 2,
        border: "1px solid #e5e7eb",
        boxShadow: "none",
        height: 86,
        minHeight: 86,
        maxHeight: 86,
        boxSizing: "border-box",
      }}
    >
      <CardContent
        sx={{
          p: 1.25,
          "&:last-child": { pb: 1.25 },
          display: "flex",
          alignItems: "center",
          gap: 1,
          height: "100%",
          boxSizing: "border-box",
        }}
      >
        <Box sx={{ backgroundColor: DIALPAD_LIGHT, borderRadius: 1, p: 0.5, color: accent, flexShrink: 0 }}>
          <Icon sx={{ fontSize: 16 }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4, fontSize: "0.58rem", display: "block", lineHeight: 1.2 }}
          >
            {title}
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 800, color: accent, lineHeight: 1.1 }}>
            {value ?? "—"}
          </Typography>
          {sub && (
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.58rem", display: "block", mt: 0.5 }}>
              {sub}
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}

const AdminDialpadDashboardPage = () => {
  const navigate = useNavigate();

  // Stats
  const [stats, setStats] = useState(null);
  const [statsError, setStatsError] = useState(null);

  // Call history
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [callsError, setCallsError] = useState(null);
  const [page, setPage] = useState(0);
  const [direction, setDirection] = useState("");
  const [allocated, setAllocated] = useState("all");
  const [dateStart, setDateStart] = useState(null);
  const [dateEnd, setDateEnd] = useState(null);
  const [search, setSearch] = useState("");
  const [openCall, setOpenCall] = useState(null);
  const [allocatingCall, setAllocatingCall] = useState(null);
  const [archivingId, setArchivingId] = useState(null);

  useEffect(() => {
    phappsRequest(API_PATHS.DIALPAD_DASHBOARD, "GET")
      .then(setStats)
      .catch(() => setStatsError("Failed to load Dialpad stats"));
  }, []);

  const fetchCalls = useCallback(async () => {
    setLoading(true);
    setCallsError(null);
    try {
      const params = new URLSearchParams({ page: page + 1, limit: 50, allocated });
      if (direction) params.set("direction", direction);
      if (dateStart) params.set("date_start", dateStart);
      if (dateEnd) params.set("date_end", dateEnd);
      const data = await phappsRequest(`${API_PATHS.DIALPAD_CALLS}?${params}`, "GET");
      setRows(data?.calls ?? []);
      setTotal(data?.total ?? 0);
    } catch {
      setCallsError("Failed to load call history");
    } finally {
      setLoading(false);
    }
  }, [page, direction, allocated, dateStart, dateEnd]);

  useEffect(() => { fetchCalls(); }, [fetchCalls]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r =>
      (r.external_number || "").includes(q) ||
      (r.manager_name || "").toLowerCase().includes(q) ||
      (r.chemist_name || "").toLowerCase().includes(q)
    );
  }, [rows, search]);

  const weekCalls = stats?.this_week?.calls ?? 0;
  const monthCalls = stats?.this_month?.calls ?? 0;
  const weekDuration = stats?.this_week?.total_duration_seconds ?? 0;
  const avgDuration = stats?.avg_duration ?? 0;
  const [unallocatedOffset, setUnallocatedOffset] = useState(0);
  const unallocated = Math.max(0, (stats?.unallocated_count ?? 0) - unallocatedOffset);

  const columns = [
    {
      field: "start_time", headerName: "Date / Time", width: 170,
      renderCell: p => <Typography variant="body2" component="span">{fmtDateTime(p.value)}</Typography>,
    },
    {
      field: "direction", headerName: "Dir", width: 55, align: "center", headerAlign: "center",
      renderCell: p => p.value === "inbound"
        ? <CallReceivedIcon fontSize="small" sx={{ color: "#059669" }} />
        : <CallMadeIcon fontSize="small" sx={{ color: "#2563eb" }} />,
    },
    { field: "external_number", headerName: "Number", width: 145 },
    {
      field: "duration_seconds", headerName: "Duration", width: 90,
      renderCell: p => <Typography variant="body2" component="span">{fmtDuration(p.value)}</Typography>,
    },
    { field: "manager_name", headerName: "Manager", flex: 1, minWidth: 110 },
    {
      field: "chemist_name", headerName: "Chemist", flex: 1, minWidth: 130,
      renderCell: p => <Typography variant="body2" component="span">{p.value || "—"}</Typography>,
    },
    {
      field: "ai_summary", headerName: "Summary", flex: 2, minWidth: 180,
      renderCell: p => {
        if (!p.value) return null;
        try {
          const parsed = JSON.parse(p.value);
          const text = parsed?.summary ?? parsed?.body ?? parsed?.content ?? null;
          if (!text) return null;
          const truncated = text.length > 90 ? text.slice(0, 90) + "…" : text;
          return (
            <Box sx={{ width: "100%", overflow: "hidden" }}>
              <Typography
                variant="caption"
                color="text.secondary"
                title={text}
                sx={{
                  display: "block",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  lineHeight: 1.4,
                  width: "100%",
                }}
              >
                {truncated}
              </Typography>
            </Box>
          );
        } catch { return null; }
      },
    },
    {
      field: "has_ai_summary", headerName: "AI", width: 50, align: "center", headerAlign: "center",
      renderCell: p => p.value
        ? <Chip label="AI" size="small" sx={{ height: 18, fontSize: "0.6rem", backgroundColor: "#ede9fe", color: DIALPAD_PINK }} />
        : null,
    },
    {
      field: "auto_matched", headerName: "Auto", width: 58, align: "center", headerAlign: "center",
      renderCell: p => p.value
        ? <Chip label="Auto" size="small" color="success" variant="outlined" sx={{ height: 18, fontSize: "0.6rem" }} />
        : null,
    },
    {
      field: "actions", headerName: "", width: 190, sortable: false,
      renderCell: p => {
        const isUnallocated = !p.row.chemist_id && !p.row.card_id && !p.row.is_archived;
        return (
          <Box sx={{ display: "flex", gap: 0.5, alignItems: "center", width: "100%", overflow: "visible" }}>
            <Button
              size="small"
              startIcon={<OpenInNewIcon sx={{ fontSize: "13px !important" }} />}
              onClick={(e) => { e.stopPropagation(); setOpenCall(p.row); }}
              sx={{ textTransform: "none", fontSize: "0.7rem", color: DIALPAD_PINK, px: 0.75, flexShrink: 0 }}
            >
              View
            </Button>
            {isUnallocated && (
              <>
                <Button
                  size="small"
                  variant="contained"
                  onClick={(e) => { e.stopPropagation(); setAllocatingCall(p.row); }}
                  sx={{
                    textTransform: "none", fontSize: "0.7rem", px: 1,
                    backgroundColor: DIALPAD_PINK, color: "#fff", flexShrink: 0,
                    "&:hover": { backgroundColor: "#c42d72" },
                    minWidth: 0,
                  }}
                >
                  Allocate
                </Button>
                <Tooltip title="Archive — ignore this call">
                  <span>
                    <IconButton
                      size="small"
                      disabled={archivingId === p.row.id}
                      onClick={async (e) => {
                        e.stopPropagation();
                        setArchivingId(p.row.id);
                        try {
                          await phappsRequest(API_PATHS.DIALPAD_CALLS_ARCHIVE(p.row.id), "POST", {});
                          setUnallocatedOffset(prev => prev + 1);
                          fetchCalls();
                        } catch { /* silent */ }
                        finally { setArchivingId(null); }
                      }}
                      sx={{ color: "#9ca3af", "&:hover": { color: "#6b7280" } }}
                    >
                      <ArchiveOutlinedIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </span>
                </Tooltip>
              </>
            )}
          </Box>
        );
      },
    },
  ];

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", boxSizing: "border-box", p: 2, gap: 1.25, overflow: "hidden" }}>

      {/* Header row */}
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.5, flexShrink: 0 }}>
        <Typography variant="h6" sx={{ fontWeight: 800, color: DIALPAD_PINK, lineHeight: 1 }}>
          Dialpad
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Call activity and history
        </Typography>
      </Box>

      {statsError && <Alert severity="warning" sx={{ flexShrink: 0 }}>{statsError}</Alert>}

      {/* 4 stat cards */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: 1,
          flexShrink: 0,
        }}
      >
        <DialpadStatCard title="Calls this week"       value={weekCalls}               Icon={PhoneIcon}        accent={DIALPAD_PINK}   />
        <DialpadStatCard title="Calls this month"      value={monthCalls}              Icon={PhoneIcon}        accent={DIALPAD_PURPLE} />
        <DialpadStatCard title="Total call time (week)" value={fmtDuration(weekDuration)} Icon={TimerOutlinedIcon} accent={DIALPAD_ORANGE} />
        <DialpadStatCard title="Avg call duration (week)" value={fmtDuration(avgDuration)} Icon={SpeedIcon} accent={DIALPAD_PURPLE} />
      </Box>

      {unallocated > 0 && (
        <Box sx={{ flexShrink: 0, px: 1.25, py: 0.75, borderRadius: 1.5, backgroundColor: "#fffbeb", border: "1px solid #fde68a", display: "flex", alignItems: "center", gap: 1 }}>
          <WarningAmberIcon sx={{ color: "#d97706", fontSize: 16 }} />
          <Typography variant="caption" sx={{ fontWeight: 600, color: "#92400e", flex: 1 }}>
            {unallocated} call{unallocated !== 1 ? "s" : ""} not yet linked to a chemist or job.
          </Typography>
          <Button size="small" onClick={() => navigate("/admin-functions/dialpad/unallocated")}
            sx={{ textTransform: "none", color: "#92400e", fontWeight: 700, fontSize: "0.72rem", py: 0, minWidth: 0 }}>
            Review →
          </Button>
        </Box>
      )}

      {/* Filters — flat bar, no card wrapper */}
      <Box sx={{ flexShrink: 0, display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center",
        borderRadius: 1.5, border: "1px solid #e5e7eb", px: 1.25, py: 0.75, backgroundColor: "#fafafa" }}>
        <TextField
          size="small" placeholder="Search number, manager, chemist…"
          value={search} onChange={e => setSearch(e.target.value)}
          sx={{ width: 240 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16 }} /></InputAdornment> }}
        />
        <FormControl size="small" sx={{ minWidth: 110 }}>
          <InputLabel>Direction</InputLabel>
          <Select label="Direction" value={direction} onChange={e => { setDirection(e.target.value); setPage(0); }}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="inbound">Inbound</MenuItem>
            <MenuItem value="outbound">Outbound</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Allocated</InputLabel>
          <Select label="Allocated" value={allocated} onChange={e => { setAllocated(e.target.value); setPage(0); }}>
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="yes">Allocated</MenuItem>
            <MenuItem value="no">Unallocated</MenuItem>
          </Select>
        </FormControl>
        <SFDateRangeSelector
          dateStart={dateStart} dateEnd={dateEnd}
          onDateStartChange={v => { setDateStart(v); setPage(0); }}
          onDateEndChange={v => { setDateEnd(v); setPage(0); }}
        />
      </Box>

      {callsError && <Alert severity="warning" sx={{ flexShrink: 0 }}>{callsError}</Alert>}

      {/* Data grid — flex-1 so it fills all remaining height */}
      <Box sx={{ flex: 1, minHeight: 0, width: "100%" }}>
        <DataGrid
          rows={filtered.map((r, i) => ({ ...r, id: r.id ?? i }))}
          columns={columns}
          loading={loading}
          paginationMode="server"
          rowCount={total}
          paginationModel={{ page, pageSize: 50 }}
          onPaginationModelChange={m => setPage(m.page)}
          pageSizeOptions={[50]}
          disableColumnMenu
          disableRowSelectionOnClick
          density="compact"
          sx={{
            height: "100%",
            ...pharmacyHaloDataGridSx,
            "& .MuiDataGrid-columnHeader": { backgroundColor: "#F58A2E", color: "#fff" },
            "& .MuiDataGrid-row:hover": { backgroundColor: "#FFFBEB !important" },
            "& .MuiDataGrid-cell": { alignItems: "center" },
            "& .MuiDataGrid-cell[data-field='actions']": { overflow: "visible !important" },
          }}
        />
      </Box>

      <CallDetailModal open={!!openCall} onClose={() => setOpenCall(null)} call={openCall} />

      {/* Allocate modal — chemist-picker mode (pass the unallocated call) */}
      <AllocateCallModal
        open={!!allocatingCall}
        onClose={() => setAllocatingCall(null)}
        call={allocatingCall}
        onAllocated={() => {
          setAllocatingCall(null);
          setUnallocatedOffset(prev => prev + 1); // hide banner immediately
          fetchCalls();
        }}
      />
    </Box>
  );
};

export default AdminDialpadDashboardPage;
